/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog, LoadingSection, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /**
   * Fired once both loading stages finish so the orchestrator can transition
   * to the Review-and-send screen.
   */
  onComplete: () => void;
  /**
   * Duration (ms) each of the two loading stages runs for. Exposed so tests
   * can pin it short instead of waiting on the wall clock.
   */
  stageDurationMs?: number;
};

const DEFAULT_STAGE_DURATION_MS = 1500;

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-200);
    min-height: 240px;
    padding: var(--cds-spacing-400) 0;
  `,
};

/**
 * Two-stage loading state shown between Define-goal and Review-and-send.
 * Sequentially flashes "Defining the right audience…" then "Crafting the
 * perfect message…" before calling `onComplete`. Stand-in for two future
 * sequential API calls (audience resolution + message generation).
 */
const GeneratingScreen = ({ onComplete, stageDurationMs = DEFAULT_STAGE_DURATION_MS }: Props) => {
  const [stage, setStage] = React.useState<'audience' | 'message'>('audience');

  React.useEffect(() => {
    // Chain the two timers so the second only starts after the first
    // fires. Running them from the same origin (stageDurationMs * 2) lets
    // both resolve in the same tick under very short test durations,
    // skipping the "Crafting the perfect message…" caption entirely.
    let messageTimer: number | undefined;
    const audienceTimer = window.setTimeout(() => {
      setStage('message');
      messageTimer = window.setTimeout(onComplete, stageDurationMs);
    }, stageDurationMs);
    return () => {
      window.clearTimeout(audienceTimer);
      if (messageTimer !== undefined) window.clearTimeout(messageTimer);
    };
  }, [onComplete, stageDurationMs]);

  const caption = stage === 'audience' ? _t('Defining the right audience…') : _t('Crafting the perfect message…');

  return (
    <Dialog.Content>
      <div css={styles.body} aria-live="polite" data-testid="ai-message-modal-generating">
        <LoadingSection role="presentation" />
        <Typography2 component="p" variant="bodyPrimary">
          {caption}
        </Typography2>
      </div>
    </Dialog.Content>
  );
};

export default GeneratingScreen;
